/**
 * Namespace Schema - v13 Section 8.12
 *
 * All namespaces follow LangGraph Store pattern: [namespace, userId, subkey?]
 *
 * @see docs/architecture/extracts/namespaces-8.12.md
 * @see docs/architecture/OwnYou_architecture_v13.md Section 8.12
 */
/**
 * NAMESPACES - All v13 namespace string constants
 *
 * Namespaces are used to organize data in the LangGraph Store.
 */
export const NAMESPACES = {
    // Core memory (Section 8.4)
    SEMANTIC_MEMORY: 'ownyou.semantic',
    EPISODIC_MEMORY: 'ownyou.episodic',
    PROCEDURAL_MEMORY: 'ownyou.procedural',
    // Relational (Section 8.4.4)
    ENTITIES: 'ownyou.entities',
    RELATIONSHIPS: 'ownyou.relationships',
    COMMUNITY_SUMMARIES: 'ownyou.summaries',
    // Classifications
    IAB_CLASSIFICATIONS: 'ownyou.iab',
    // Ikigai (Section 2)
    IKIGAI_PROFILE: 'ownyou.ikigai',
    IKIGAI_EVIDENCE: 'ownyou.ikigai_evidence',
    // Missions (Section 3)
    MISSION_CARDS: 'ownyou.missions',
    MISSION_FEEDBACK: 'ownyou.mission_feedback',
    // Identity (Section 7)
    PSEUDONYMS: 'ownyou.pseudonyms',
    DISCLOSURE_HISTORY: 'ownyou.disclosures',
    TRACKING_CONSENTS: 'ownyou.tracking_consents',
    // Financial
    EARNINGS: 'ownyou.earnings',
    // Data Source Configuration (v13 Section 5 - User-configurable sync settings)
    DATA_SOURCE_CONFIGS: 'ownyou.data_source_configs',
    // Observability (Section 10)
    AGENT_TRACES: 'ownyou.traces',
    LLM_USAGE: 'ownyou.llm_usage',
    SYNC_LOGS: 'ownyou.sync_logs',
    AUDIT_EVENTS: 'ownyou.audit_events',
    // LLM Budget (v13 Section 6.10 - Budget Enforcement)
    LLM_BUDGET: 'ownyou.llm_budget',
    // LLM Cache (v13 Section 6.11.3 - Fallback Chain Step 5)
    LLM_CACHE: 'ownyou.llm_cache',
    // Restaurant Agent (Sprint 7)
    DINING_RESERVATIONS: 'ownyou.dining_reservations',
    RESTAURANT_FAVORITES: 'ownyou.restaurant_favorites',
    // Events Agent (Sprint 7)
    EVENT_TICKETS: 'ownyou.event_tickets',
    EVENT_FAVORITES: 'ownyou.event_favorites',
    // Travel Agent (Sprint 7)
    TRAVEL_ITINERARIES: 'ownyou.travel_itineraries',
    TRAVEL_PREFERENCES: 'ownyou.travel_preferences',
    // Calendar (Sprint 7 - Events & Travel Agents)
    CALENDAR: 'ownyou.calendar',
    // Financial Profile (Sprint 7 - Travel Agent)
    FINANCIAL_PROFILE: 'ownyou.financial_profile',
    // Interests (Sprint 7 - Events Agent)
    INTERESTS: 'ownyou.interests',
    // Reflection (v13 Section 8.12 - Reflection State)
    REFLECTION_STATE: 'ownyou.reflection',
    // Sprint 8: Financial Data
    FINANCIAL_TRANSACTIONS: 'ownyou.financial_transactions',
    // Sprint 8: Calendar Data
    CALENDAR_EVENTS: 'ownyou.calendar_events',
    CALENDAR_PROFILE: 'ownyou.calendar_profile',
    CALENDAR_CONTACTS: 'ownyou.calendar_contacts', // Relationship extraction from attendees
    // Sprint 8: Diagnostic Agent
    DIAGNOSTIC_REPORTS: 'ownyou.diagnostic_reports',
    // Sprint 10: Cross-Device Sync (v13 Section 5.2, 8.14)
    SYNC_STATE: 'ownyou.sync_state', // Vector clocks, conflict resolution state
    SYNC_PEERS: 'ownyou.sync_peers', // Known peer devices
    DEVICE_REGISTRY: 'ownyou.devices', // Registered devices for this wallet
    // Sprint 10: E2EE Backup (v13 Section 5.3, 5.4)
    BACKUP_MANIFEST: 'ownyou.backup_manifest', // Backup metadata, checksums
    BACKUP_HISTORY: 'ownyou.backup_history', // Backup/restore history
    RECOVERY_KEY_HASH: 'ownyou.recovery_key_hash', // Hash of recovery key for verification
    // Sprint 11: UI State (Consumer App)
    UI_PREFERENCES: 'ownyou.ui.preferences', // Theme, display settings
    UI_FILTER_STATE: 'ownyou.ui.filterState', // Current filter tab, view state
    // Sprint 11c: A/B Testing (Consumer App)
    AB_TESTING_RESULTS: 'ownyou.ab_testing.results', // A/B testing classification results
    AB_TESTING_STAGE1: 'ownyou.ab_testing.stage1', // Downloaded emails
    AB_TESTING_STAGE2: 'ownyou.ab_testing.stage2', // Preprocessed emails
    // Sprint 9: Debug Namespaces (v13 Section 10.7)
    DEBUG_TRACES: 'ownyou.debug.traces', // Agent execution traces with full detail
    DEBUG_SYNC: 'ownyou.debug.sync', // Sync operation logs per device
    DEBUG_LLM: 'ownyou.debug.llm', // LLM usage metrics and cost tracking
    DEBUG_ERRORS: 'ownyou.debug.errors', // Error logs for debugging
    DEBUG_AUDIT: 'ownyou.debug.audit', // Debug-only local audit telemetry, not compliance evidence
    // User Profile & Preferences (used by sync, backup, observability)
    PREFERENCES: 'ownyou.preferences', // General user preferences (distinct from UI_PREFERENCES)
    USER_NAME: 'ownyou.user.name', // User display name
    NOTIFICATIONS_SETTINGS: 'ownyou.notifications.settings', // Notification configuration
    // Gamification Counters (CRDT sync)
    IKIGAI_POINTS: 'ownyou.ikigai.points', // Ikigai engagement points (increment-only)
    MISSIONS_COMPLETION_COUNT: 'ownyou.missions.completionCount', // Mission completion counter
    MISSIONS_TAGS: 'ownyou.missions.tags', // Mission tag set (add/remove)
    MISSIONS_DISMISSED: 'ownyou.missions.dismissed', // Dismissed mission IDs
    // Token Economy (CRDT sync)
    TOKEN_BALANCE: 'ownyou.token.balance', // Token balance (increment/decrement)
    // Sync Infrastructure (CRDT sync)
    SYNC_TRUSTED_PEERS: 'ownyou.sync.trustedPeers', // Trusted peer device set
    SYNC_LAST_TIMESTAMP: 'ownyou.sync.lastTimestamp', // Last sync timestamp per device
    // Data Source CRDT Config (distinct from DATA_SOURCE_CONFIGS which is Store-based)
    DATA_SOURCES_CRDT_CONFIGS: 'ownyou.dataSources.configs', // CRDT-synced data source settings
    // Backup-specific namespace (missions completions for realtime backup)
    MISSIONS_COMPLETIONS: 'ownyou.missions.completions', // Mission completion records
    // Browsing History (Browser Extension)
    BROWSING_HISTORY: 'ownyou.browsing_history',
    // Sprint 16: Love Entity Detection
    LOVE_THINGS: 'ownyou.love.things',
    LOVE_EXPERIENCES: 'ownyou.love.experiences',
    LOVE_PEOPLE: 'ownyou.love.people',
    // Sprint 16b: Detection Pipeline
    IKIGAI_METRICS: 'ownyou.ikigai.metrics',
    IKIGAI_FEEDBACK: 'ownyou.ikigai.feedback',
    IKIGAI_DETECTION_STATE: 'ownyou.ikigai.detection_state',
    IKIGAI_OPPORTUNITIES: 'ownyou.ikigai.opportunities',
    // Pipeline Transparency (Sprint 16+)
    PIPELINE_TRACES: 'ownyou.pipeline.traces',
    PIPELINE_TRACE_DATA: 'ownyou.pipeline.trace_data',
    // Sprint 17d: Rewards
    REWARDS_TOTALS: 'ownyou.rewards.totals',
    REWARDS_TRANSACTIONS: 'ownyou.rewards.transactions',
    // Sprint 17e: Refresh metadata
    PIPELINE_METADATA: 'ownyou.pipeline_metadata',
    // Sprint 17g: Unified User Profile
    USER_PROFILE: 'ownyou.user_profile',
    ENGAGEMENT_EVENTS: 'ownyou.engagement_events',
    // Sprint 17g-4: UX Remediation
    REFINEMENT_HISTORY: 'ownyou.refinement_history',
    PREDICTION_ACCURACY: 'ownyou.prediction_accuracy',
    CHARACTER_SUMMARY: 'ownyou.character_summary',
    // Consumer-refactor Slice 08: Welcome first-run outcomes
    WELCOME_OUTCOMES: 'ownyou.welcome.outcomes',
};
/**
 * NS - Namespace tuple factory functions
 *
 * Creates properly typed namespace tuples for LangGraph Store operations.
 */
export const NS = {
    /** Semantic memory namespace: [namespace, userId] */
    semanticMemory: (userId) => [NAMESPACES.SEMANTIC_MEMORY, userId],
    /** Episodic memory namespace: [namespace, userId] */
    episodicMemory: (userId) => [NAMESPACES.EPISODIC_MEMORY, userId],
    /** Procedural memory namespace: [namespace, userId, agentType] */
    proceduralMemory: (userId, agentType) => [NAMESPACES.PROCEDURAL_MEMORY, userId, agentType],
    /** Entities namespace: [namespace, userId] */
    entities: (userId) => [NAMESPACES.ENTITIES, userId],
    /** Relationships namespace: [namespace, userId] */
    relationships: (userId) => [NAMESPACES.RELATIONSHIPS, userId],
    /** IAB classifications namespace: [namespace, userId] */
    iabClassifications: (userId) => [NAMESPACES.IAB_CLASSIFICATIONS, userId],
    /** Ikigai profile namespace: [namespace, userId] */
    ikigaiProfile: (userId) => [NAMESPACES.IKIGAI_PROFILE, userId],
    /** Ikigai evidence namespace: [namespace, userId] */
    ikigaiEvidence: (userId) => [NAMESPACES.IKIGAI_EVIDENCE, userId],
    /** Mission cards namespace: [namespace, userId] */
    missionCards: (userId) => [NAMESPACES.MISSION_CARDS, userId],
    /** Mission feedback namespace: [namespace, userId] */
    missionFeedback: (userId) => [NAMESPACES.MISSION_FEEDBACK, userId],
    /** Pseudonyms namespace: [namespace, userId] */
    pseudonyms: (userId) => [NAMESPACES.PSEUDONYMS, userId],
    /** Earnings namespace: [namespace, userId] */
    earnings: (userId) => [NAMESPACES.EARNINGS, userId],
    /** Data source configs namespace: [namespace, userId] - Sprint 11b Deviation 1 fix */
    dataSourceConfigs: (userId) => [NAMESPACES.DATA_SOURCE_CONFIGS, userId],
    /** Agent traces namespace: [namespace, userId] */
    agentTraces: (userId) => [NAMESPACES.AGENT_TRACES, userId],
    /** Audit events namespace: [namespace, userId] - first-class body-safe AuditLog stream */
    auditEvents: (userId) => [NAMESPACES.AUDIT_EVENTS, userId],
    /** Sync logs namespace: [namespace, userId] - pipeline session logs */
    syncLogs: (userId) => [NAMESPACES.SYNC_LOGS, userId],
    /** LLM usage namespace: [namespace, userId, period] */
    llmUsage: (userId, period) => [NAMESPACES.LLM_USAGE, userId, period],
    /** LLM budget namespace: [namespace, userId] - for budget tracking (v13 6.10) */
    llmBudget: (userId) => [NAMESPACES.LLM_BUDGET, userId],
    /** LLM cache namespace: [namespace, userId] - for response caching (v13 6.11.3) */
    llmCache: (userId) => [NAMESPACES.LLM_CACHE, userId],
    // Sprint 7: Restaurant Agent namespaces
    /** Dining reservations namespace: [namespace, userId] */
    diningReservations: (userId) => [NAMESPACES.DINING_RESERVATIONS, userId],
    /** Restaurant favorites namespace: [namespace, userId] */
    restaurantFavorites: (userId) => [NAMESPACES.RESTAURANT_FAVORITES, userId],
    // Sprint 7: Events Agent namespaces
    /** Event tickets namespace: [namespace, userId] */
    eventTickets: (userId) => [NAMESPACES.EVENT_TICKETS, userId],
    /** Event favorites namespace: [namespace, userId] */
    eventFavorites: (userId) => [NAMESPACES.EVENT_FAVORITES, userId],
    // Sprint 7: Travel Agent namespaces
    /** Travel itineraries namespace: [namespace, userId] */
    travelItineraries: (userId) => [NAMESPACES.TRAVEL_ITINERARIES, userId],
    /** Travel preferences namespace: [namespace, userId] */
    travelPreferences: (userId) => [NAMESPACES.TRAVEL_PREFERENCES, userId],
    // Sprint 7: Calendar namespace (Events & Travel Agents)
    /** Calendar namespace: [namespace, userId] */
    calendar: (userId) => [NAMESPACES.CALENDAR, userId],
    // Sprint 7: Financial profile namespace (Travel Agent)
    /** Financial profile namespace: [namespace, userId] */
    financialProfile: (userId) => [NAMESPACES.FINANCIAL_PROFILE, userId],
    // Sprint 7: Interests namespace (Events Agent)
    /** Interests namespace: [namespace, userId] */
    interests: (userId) => [NAMESPACES.INTERESTS, userId],
    // Reflection State (v13 Section 8.12)
    /** Reflection state namespace: [namespace, userId] */
    reflectionState: (userId) => [NAMESPACES.REFLECTION_STATE, userId],
    /** Community summaries namespace: [namespace, userId] */
    communitySummaries: (userId) => [NAMESPACES.COMMUNITY_SUMMARIES, userId],
    // Sprint 8: Financial Data namespaces
    /** Financial transactions namespace: [namespace, userId] */
    financialTransactions: (userId) => [NAMESPACES.FINANCIAL_TRANSACTIONS, userId],
    // Sprint 8: Calendar Data namespaces
    /** Calendar events namespace: [namespace, userId] */
    calendarEvents: (userId) => [NAMESPACES.CALENDAR_EVENTS, userId],
    /** Calendar profile namespace: [namespace, userId] */
    calendarProfile: (userId) => [NAMESPACES.CALENDAR_PROFILE, userId],
    /** Calendar contacts namespace: [namespace, userId] - relationship extraction */
    calendarContacts: (userId) => [NAMESPACES.CALENDAR_CONTACTS, userId],
    // Sprint 8: Diagnostic Agent namespace
    /** Diagnostic reports namespace: [namespace, userId] */
    diagnosticReports: (userId) => [NAMESPACES.DIAGNOSTIC_REPORTS, userId],
    // Sprint 10: Cross-Device Sync namespaces
    /** Sync state namespace: [namespace, userId] - vector clocks, conflict resolution */
    syncState: (userId) => [NAMESPACES.SYNC_STATE, userId],
    /** Sync peers namespace: [namespace, userId] - known peer devices */
    syncPeers: (userId) => [NAMESPACES.SYNC_PEERS, userId],
    /** Device registry namespace: [namespace, userId] - registered devices */
    deviceRegistry: (userId) => [NAMESPACES.DEVICE_REGISTRY, userId],
    // Sprint 10: E2EE Backup namespaces
    /** Backup manifest namespace: [namespace, userId] - backup metadata */
    backupManifest: (userId) => [NAMESPACES.BACKUP_MANIFEST, userId],
    /** Backup history namespace: [namespace, userId] - backup/restore history */
    backupHistory: (userId) => [NAMESPACES.BACKUP_HISTORY, userId],
    /** Recovery key hash namespace: [namespace, userId] - for key verification */
    recoveryKeyHash: (userId) => [NAMESPACES.RECOVERY_KEY_HASH, userId],
    // Sprint 11: UI State namespaces
    /** UI preferences namespace: [namespace, userId] */
    uiPreferences: (userId) => [NAMESPACES.UI_PREFERENCES, userId],
    /** UI filter state namespace: [namespace, userId] */
    uiFilterState: (userId) => [NAMESPACES.UI_FILTER_STATE, userId],
    // Sprint 11c: A/B Testing namespaces
    /** A/B testing results namespace: [namespace, userId] */
    abTestingResults: (userId) => [NAMESPACES.AB_TESTING_RESULTS, userId],
    /** A/B testing stage 1 namespace: [namespace, userId] */
    abTestingStage1: (userId) => [NAMESPACES.AB_TESTING_STAGE1, userId],
    /** A/B testing stage 2 namespace: [namespace, userId] */
    abTestingStage2: (userId) => [NAMESPACES.AB_TESTING_STAGE2, userId],
    // Sprint 9: Debug namespace factory functions (v13 Section 10.7)
    /** Debug traces namespace: [namespace, userId] - agent execution traces */
    debugTraces: (userId) => [NAMESPACES.DEBUG_TRACES, userId],
    /** Debug sync namespace: [namespace, deviceId] - sync logs per device */
    debugSync: (deviceId) => [NAMESPACES.DEBUG_SYNC, deviceId],
    /** Debug LLM namespace: [namespace, userId] - LLM usage metrics */
    debugLlm: (userId) => [NAMESPACES.DEBUG_LLM, userId],
    /** Debug errors namespace: [namespace, userId] - error logs */
    debugErrors: (userId) => [NAMESPACES.DEBUG_ERRORS, userId],
    /** Debug audit namespace: [namespace, userId] - debug-only local audit telemetry */
    debugAudit: (userId) => [NAMESPACES.DEBUG_AUDIT, userId],
    // User Profile & Preferences
    /** Preferences namespace: [namespace, userId] */
    preferences: (userId) => [NAMESPACES.PREFERENCES, userId],
    /** User name namespace: [namespace, userId] */
    userName: (userId) => [NAMESPACES.USER_NAME, userId],
    /** Notifications settings namespace: [namespace, userId] */
    notificationsSettings: (userId) => [NAMESPACES.NOTIFICATIONS_SETTINGS, userId],
    // Gamification Counters
    /** Ikigai points namespace: [namespace, userId] */
    ikigaiPoints: (userId) => [NAMESPACES.IKIGAI_POINTS, userId],
    /** Missions completion count namespace: [namespace, userId] */
    missionsCompletionCount: (userId) => [NAMESPACES.MISSIONS_COMPLETION_COUNT, userId],
    /** Missions tags namespace: [namespace, userId] */
    missionsTags: (userId) => [NAMESPACES.MISSIONS_TAGS, userId],
    /** Missions dismissed namespace: [namespace, userId] */
    missionsDismissed: (userId) => [NAMESPACES.MISSIONS_DISMISSED, userId],
    // Token Economy
    /** Token balance namespace: [namespace, userId] */
    tokenBalance: (userId) => [NAMESPACES.TOKEN_BALANCE, userId],
    // Sync Infrastructure
    /** Sync trusted peers namespace: [namespace, userId] */
    syncTrustedPeers: (userId) => [NAMESPACES.SYNC_TRUSTED_PEERS, userId],
    /** Sync last timestamp namespace: [namespace, userId] */
    syncLastTimestamp: (userId) => [NAMESPACES.SYNC_LAST_TIMESTAMP, userId],
    // Data Source CRDT Config
    /** Data sources CRDT configs namespace: [namespace, userId] */
    dataSourcesCrdtConfigs: (userId) => [NAMESPACES.DATA_SOURCES_CRDT_CONFIGS, userId],
    // Backup-specific
    /** Missions completions namespace: [namespace, userId] */
    missionsCompletions: (userId) => [NAMESPACES.MISSIONS_COMPLETIONS, userId],
    // Browsing History
    browsingHistory: (userId) => [NAMESPACES.BROWSING_HISTORY, userId],
    // Sprint 16: Love Entity Detection
    loveThings: (userId) => [NAMESPACES.LOVE_THINGS, userId],
    loveExperiences: (userId) => [NAMESPACES.LOVE_EXPERIENCES, userId],
    lovePeople: (userId) => [NAMESPACES.LOVE_PEOPLE, userId],
    // Sprint 16b: Detection Pipeline
    ikigaiMetrics: (userId) => [NAMESPACES.IKIGAI_METRICS, userId],
    ikigaiFeedback: (userId) => [NAMESPACES.IKIGAI_FEEDBACK, userId],
    ikigaiDetectionState: (userId) => [NAMESPACES.IKIGAI_DETECTION_STATE, userId],
    ikigaiOpportunities: (userId) => [NAMESPACES.IKIGAI_OPPORTUNITIES, userId],
    // Pipeline Transparency
    pipelineTraces: (userId) => [NAMESPACES.PIPELINE_TRACES, userId],
    pipelineTraceData: (userId) => [NAMESPACES.PIPELINE_TRACE_DATA, userId],
    // Sprint 17d: Rewards
    rewardsTotals: (userId) => [NAMESPACES.REWARDS_TOTALS, userId],
    rewardsTransactions: (userId) => [NAMESPACES.REWARDS_TRANSACTIONS, userId],
    // Sprint 17e: Refresh metadata
    pipelineMetadata: (userId) => [NAMESPACES.PIPELINE_METADATA, userId],
    // Sprint 17g: Unified User Profile
    userProfile: (userId) => [NAMESPACES.USER_PROFILE, userId],
    engagementEvents: (userId) => [NAMESPACES.ENGAGEMENT_EVENTS, userId],
    // Sprint 17g-4: UX Remediation
    refinementHistory: (userId) => [NAMESPACES.REFINEMENT_HISTORY, userId],
    predictionAccuracy: (userId) => [NAMESPACES.PREDICTION_ACCURACY, userId],
    characterSummary: (userId) => [NAMESPACES.CHARACTER_SUMMARY, userId],
    // Consumer-refactor Slice 08: Welcome first-run outcomes
    welcomeOutcomes: (userId) => [NAMESPACES.WELCOME_OUTCOMES, userId],
};
/**
 * NAMESPACE_PRIVACY - Privacy tier by namespace (v13 Section 8.11)
 *
 * Defines the privacy classification for each namespace.
 */
export const NAMESPACE_PRIVACY = {
    [NAMESPACES.SEMANTIC_MEMORY]: 'public',
    [NAMESPACES.EPISODIC_MEMORY]: 'public',
    [NAMESPACES.PROCEDURAL_MEMORY]: 'public',
    [NAMESPACES.ENTITIES]: 'public',
    [NAMESPACES.RELATIONSHIPS]: 'sensitive',
    [NAMESPACES.COMMUNITY_SUMMARIES]: 'public',
    [NAMESPACES.IAB_CLASSIFICATIONS]: 'public',
    [NAMESPACES.IKIGAI_PROFILE]: 'sensitive',
    [NAMESPACES.IKIGAI_EVIDENCE]: 'sensitive',
    [NAMESPACES.MISSION_CARDS]: 'public',
    [NAMESPACES.MISSION_FEEDBACK]: 'public',
    [NAMESPACES.PSEUDONYMS]: 'private',
    [NAMESPACES.DISCLOSURE_HISTORY]: 'private',
    [NAMESPACES.TRACKING_CONSENTS]: 'private',
    [NAMESPACES.EARNINGS]: 'sensitive',
    [NAMESPACES.DATA_SOURCE_CONFIGS]: 'private', // User data source configs - device-local
    [NAMESPACES.AGENT_TRACES]: 'private',
    [NAMESPACES.LLM_USAGE]: 'private',
    [NAMESPACES.SYNC_LOGS]: 'private',
    [NAMESPACES.AUDIT_EVENTS]: 'private',
    [NAMESPACES.LLM_BUDGET]: 'private', // User's LLM budget tracking
    [NAMESPACES.LLM_CACHE]: 'private', // Cached LLM responses - device-local
    // Sprint 7: Restaurant Agent namespaces
    [NAMESPACES.DINING_RESERVATIONS]: 'sensitive', // Reservation details
    [NAMESPACES.RESTAURANT_FAVORITES]: 'public', // Public preferences
    // Sprint 7: Events Agent namespaces
    [NAMESPACES.EVENT_TICKETS]: 'sensitive', // Ticket purchase info
    [NAMESPACES.EVENT_FAVORITES]: 'public', // Public preferences
    // Sprint 7: Travel Agent namespaces
    [NAMESPACES.TRAVEL_ITINERARIES]: 'sensitive', // Travel plans
    [NAMESPACES.TRAVEL_PREFERENCES]: 'public', // Public preferences
    // Sprint 7: Additional namespaces
    [NAMESPACES.CALENDAR]: 'private', // Calendar is private
    [NAMESPACES.FINANCIAL_PROFILE]: 'private', // Financial data is private
    [NAMESPACES.INTERESTS]: 'public', // Interests are public preferences
    // Reflection State (v13 Section 8.12)
    [NAMESPACES.REFLECTION_STATE]: 'private', // Reflection triggers are device-internal
    // Sprint 8: Financial Data
    [NAMESPACES.FINANCIAL_TRANSACTIONS]: 'private', // Financial transactions are private
    // Sprint 8: Calendar Data
    [NAMESPACES.CALENDAR_EVENTS]: 'private', // Calendar events are private
    [NAMESPACES.CALENDAR_PROFILE]: 'private', // Calendar profile is private
    [NAMESPACES.CALENDAR_CONTACTS]: 'sensitive', // Relationship data is sensitive
    // Sprint 8: Diagnostic Agent
    [NAMESPACES.DIAGNOSTIC_REPORTS]: 'private', // Diagnostic reports are private
    // Sprint 10: Cross-Device Sync
    [NAMESPACES.SYNC_STATE]: 'private', // Internal sync state
    [NAMESPACES.SYNC_PEERS]: 'private', // Peer device list
    [NAMESPACES.DEVICE_REGISTRY]: 'private', // Device registration
    // Sprint 10: E2EE Backup
    [NAMESPACES.BACKUP_MANIFEST]: 'private', // Backup metadata
    [NAMESPACES.BACKUP_HISTORY]: 'private', // Backup history
    [NAMESPACES.RECOVERY_KEY_HASH]: 'private', // Recovery key verification
    // Sprint 11: UI State
    [NAMESPACES.UI_PREFERENCES]: 'private', // User preferences are private
    [NAMESPACES.UI_FILTER_STATE]: 'private', // Filter state is private
    // Sprint 11c: A/B Testing
    [NAMESPACES.AB_TESTING_RESULTS]: 'private', // Classification results are private
    [NAMESPACES.AB_TESTING_STAGE1]: 'private', // Downloaded emails are private
    [NAMESPACES.AB_TESTING_STAGE2]: 'private', // Preprocessed emails are private
    // Sprint 9: Debug namespaces (v13 Section 10.7)
    [NAMESPACES.DEBUG_TRACES]: 'private', // Agent traces are private debug data
    [NAMESPACES.DEBUG_SYNC]: 'private', // Sync logs are private debug data
    [NAMESPACES.DEBUG_LLM]: 'private', // LLM metrics are private debug data
    [NAMESPACES.DEBUG_ERRORS]: 'private', // Error logs are private debug data
    [NAMESPACES.DEBUG_AUDIT]: 'private', // Debug audit telemetry is private debug data
    // User Profile & Preferences
    [NAMESPACES.PREFERENCES]: 'private', // General preferences are private
    [NAMESPACES.USER_NAME]: 'sensitive', // Display name is sensitive (PII)
    [NAMESPACES.NOTIFICATIONS_SETTINGS]: 'private', // Notification prefs are private
    // Gamification Counters
    [NAMESPACES.IKIGAI_POINTS]: 'public', // Points are shareable
    [NAMESPACES.MISSIONS_COMPLETION_COUNT]: 'public', // Completion count is shareable
    [NAMESPACES.MISSIONS_TAGS]: 'public', // Tags are public preferences
    [NAMESPACES.MISSIONS_DISMISSED]: 'private', // Dismissed missions are private
    // Token Economy
    [NAMESPACES.TOKEN_BALANCE]: 'private', // Financial balance is private
    // Sync Infrastructure
    [NAMESPACES.SYNC_TRUSTED_PEERS]: 'private', // Trusted peers are private
    [NAMESPACES.SYNC_LAST_TIMESTAMP]: 'private', // Sync metadata is private
    // Data Source CRDT Config
    [NAMESPACES.DATA_SOURCES_CRDT_CONFIGS]: 'private', // Data source configs are private
    // Backup-specific
    [NAMESPACES.MISSIONS_COMPLETIONS]: 'public', // Mission completions are shareable
    // Browsing History
    [NAMESPACES.BROWSING_HISTORY]: 'private', // Browsing history is private
    // Sprint 16: Love Entity Detection
    [NAMESPACES.LOVE_THINGS]: 'sensitive',
    [NAMESPACES.LOVE_EXPERIENCES]: 'sensitive',
    [NAMESPACES.LOVE_PEOPLE]: 'sensitive',
    // Sprint 16b: Detection Pipeline
    [NAMESPACES.IKIGAI_METRICS]: 'private',
    [NAMESPACES.IKIGAI_FEEDBACK]: 'private',
    [NAMESPACES.IKIGAI_DETECTION_STATE]: 'private',
    [NAMESPACES.IKIGAI_OPPORTUNITIES]: 'sensitive',
    // Pipeline Transparency
    [NAMESPACES.PIPELINE_TRACES]: 'private',
    [NAMESPACES.PIPELINE_TRACE_DATA]: 'private',
    // Sprint 17d: Rewards
    [NAMESPACES.REWARDS_TOTALS]: 'private',
    [NAMESPACES.REWARDS_TRANSACTIONS]: 'private',
    // Sprint 17e: Refresh metadata
    [NAMESPACES.PIPELINE_METADATA]: 'private',
    // Sprint 17g: Unified User Profile
    [NAMESPACES.USER_PROFILE]: 'sensitive',
    [NAMESPACES.ENGAGEMENT_EVENTS]: 'private',
    // Sprint 17g-4: UX Remediation
    [NAMESPACES.REFINEMENT_HISTORY]: 'private',
    [NAMESPACES.PREDICTION_ACCURACY]: 'private',
    [NAMESPACES.CHARACTER_SUMMARY]: 'sensitive',
    // Consumer-refactor Slice 08
    [NAMESPACES.WELCOME_OUTCOMES]: 'private',
};
/**
 * NAMESPACE_SYNC_SCOPE - Sync scope by namespace (v13 Section 8.14.1)
 *
 * Defines what data syncs across devices.
 * - 'full': All data syncs
 * - 'selective': Only recent/important data syncs
 * - 'none': Device-local only, never syncs
 */
export const NAMESPACE_SYNC_SCOPE = {
    [NAMESPACES.SEMANTIC_MEMORY]: 'full',
    [NAMESPACES.EPISODIC_MEMORY]: 'selective', // Recent 30 days + strong signals
    [NAMESPACES.PROCEDURAL_MEMORY]: 'full',
    [NAMESPACES.ENTITIES]: 'full',
    [NAMESPACES.RELATIONSHIPS]: 'full',
    [NAMESPACES.COMMUNITY_SUMMARIES]: 'full',
    [NAMESPACES.IAB_CLASSIFICATIONS]: 'full',
    [NAMESPACES.IKIGAI_PROFILE]: 'full',
    [NAMESPACES.IKIGAI_EVIDENCE]: 'selective',
    [NAMESPACES.MISSION_CARDS]: 'full',
    [NAMESPACES.MISSION_FEEDBACK]: 'full',
    [NAMESPACES.PSEUDONYMS]: 'full',
    [NAMESPACES.DISCLOSURE_HISTORY]: 'full',
    [NAMESPACES.TRACKING_CONSENTS]: 'full',
    [NAMESPACES.EARNINGS]: 'full',
    [NAMESPACES.DATA_SOURCE_CONFIGS]: 'none', // Device-local data source configs
    [NAMESPACES.AGENT_TRACES]: 'none', // Device-local
    [NAMESPACES.LLM_USAGE]: 'none', // Device-local
    [NAMESPACES.SYNC_LOGS]: 'none', // Device-local
    [NAMESPACES.AUDIT_EVENTS]: 'none', // Device-local first-class AuditLog stream for Slice 07
    [NAMESPACES.LLM_BUDGET]: 'full', // Budget syncs across devices (per-user limit)
    [NAMESPACES.LLM_CACHE]: 'none', // Device-local cache, never syncs
    // Sprint 7: Restaurant Agent namespaces
    [NAMESPACES.DINING_RESERVATIONS]: 'full', // Reservations sync
    [NAMESPACES.RESTAURANT_FAVORITES]: 'full', // Favorites sync
    // Sprint 7: Events Agent namespaces
    [NAMESPACES.EVENT_TICKETS]: 'full', // Tickets sync
    [NAMESPACES.EVENT_FAVORITES]: 'full', // Favorites sync
    // Sprint 7: Travel Agent namespaces
    [NAMESPACES.TRAVEL_ITINERARIES]: 'full', // Itineraries sync
    [NAMESPACES.TRAVEL_PREFERENCES]: 'full', // Preferences sync
    // Sprint 7: Additional namespaces
    [NAMESPACES.CALENDAR]: 'full', // Calendar syncs across devices
    [NAMESPACES.FINANCIAL_PROFILE]: 'full', // Financial profile syncs
    [NAMESPACES.INTERESTS]: 'full', // Interests sync
    // Reflection State (v13 Section 8.12 / 8.14.1)
    [NAMESPACES.REFLECTION_STATE]: 'full', // Reflection triggers must be consistent
    // Sprint 8: Financial Data
    [NAMESPACES.FINANCIAL_TRANSACTIONS]: 'selective', // Recent transactions sync
    // Sprint 8: Calendar Data
    [NAMESPACES.CALENDAR_EVENTS]: 'selective', // Recent events sync
    [NAMESPACES.CALENDAR_PROFILE]: 'full', // Profile syncs across devices
    [NAMESPACES.CALENDAR_CONTACTS]: 'full', // Relationship data syncs
    // Sprint 8: Diagnostic Agent
    [NAMESPACES.DIAGNOSTIC_REPORTS]: 'full', // Reports sync across devices
    // Sprint 10: Cross-Device Sync
    [NAMESPACES.SYNC_STATE]: 'full', // Sync state must be consistent across devices
    [NAMESPACES.SYNC_PEERS]: 'full', // Peer list syncs so devices know each other
    [NAMESPACES.DEVICE_REGISTRY]: 'full', // Device registry syncs
    // Sprint 10: E2EE Backup
    [NAMESPACES.BACKUP_MANIFEST]: 'full', // Manifest syncs so all devices know backup status
    [NAMESPACES.BACKUP_HISTORY]: 'full', // History syncs for audit
    [NAMESPACES.RECOVERY_KEY_HASH]: 'full', // Key hash syncs for verification
    // Sprint 11: UI State
    [NAMESPACES.UI_PREFERENCES]: 'full', // Theme preferences sync across devices
    [NAMESPACES.UI_FILTER_STATE]: 'none', // Device-local filter state (doesn't sync)
    // Sprint 11c: A/B Testing
    [NAMESPACES.AB_TESTING_RESULTS]: 'none', // A/B test results are device-local
    [NAMESPACES.AB_TESTING_STAGE1]: 'none', // Downloaded emails are device-local
    [NAMESPACES.AB_TESTING_STAGE2]: 'none', // Preprocessed emails are device-local
    // Sprint 9: Debug namespaces (v13 Section 10.7) - all device-local, never sync
    [NAMESPACES.DEBUG_TRACES]: 'none', // Agent traces are device-local
    [NAMESPACES.DEBUG_SYNC]: 'none', // Sync logs are device-local
    [NAMESPACES.DEBUG_LLM]: 'none', // LLM metrics are device-local
    [NAMESPACES.DEBUG_ERRORS]: 'none', // Error logs are device-local
    [NAMESPACES.DEBUG_AUDIT]: 'none', // Debug audit telemetry is device-local
    // User Profile & Preferences
    [NAMESPACES.PREFERENCES]: 'full', // Preferences sync across devices
    [NAMESPACES.USER_NAME]: 'full', // Display name syncs
    [NAMESPACES.NOTIFICATIONS_SETTINGS]: 'full', // Notification settings sync
    // Gamification Counters
    [NAMESPACES.IKIGAI_POINTS]: 'full', // Points must be consistent across devices
    [NAMESPACES.MISSIONS_COMPLETION_COUNT]: 'full', // Counter must be consistent
    [NAMESPACES.MISSIONS_TAGS]: 'full', // Tags sync across devices
    [NAMESPACES.MISSIONS_DISMISSED]: 'full', // Dismissed list syncs
    // Token Economy
    [NAMESPACES.TOKEN_BALANCE]: 'full', // Balance must be consistent across devices
    // Sync Infrastructure
    [NAMESPACES.SYNC_TRUSTED_PEERS]: 'full', // Peer list syncs
    [NAMESPACES.SYNC_LAST_TIMESTAMP]: 'full', // Sync timestamps sync
    // Data Source CRDT Config
    [NAMESPACES.DATA_SOURCES_CRDT_CONFIGS]: 'full', // Data source configs sync
    // Backup-specific
    [NAMESPACES.MISSIONS_COMPLETIONS]: 'full', // Completion records sync
    // Browsing History
    [NAMESPACES.BROWSING_HISTORY]: 'selective', // Recent browsing syncs
    // Sprint 16: Love Entity Detection
    [NAMESPACES.LOVE_THINGS]: 'full',
    [NAMESPACES.LOVE_EXPERIENCES]: 'full',
    [NAMESPACES.LOVE_PEOPLE]: 'full',
    // Sprint 16b: Detection Pipeline
    [NAMESPACES.IKIGAI_METRICS]: 'none',
    [NAMESPACES.IKIGAI_FEEDBACK]: 'selective',
    [NAMESPACES.IKIGAI_DETECTION_STATE]: 'none',
    [NAMESPACES.IKIGAI_OPPORTUNITIES]: 'full',
    // Pipeline Transparency — device-local, never syncs
    [NAMESPACES.PIPELINE_TRACES]: 'none',
    [NAMESPACES.PIPELINE_TRACE_DATA]: 'none',
    // Sprint 17d: Rewards — must be consistent across devices
    [NAMESPACES.REWARDS_TOTALS]: 'full',
    [NAMESPACES.REWARDS_TRANSACTIONS]: 'full',
    // Sprint 17e: Refresh metadata — device-local
    [NAMESPACES.PIPELINE_METADATA]: 'none',
    // Sprint 17g: Unified User Profile
    [NAMESPACES.USER_PROFILE]: 'full',
    [NAMESPACES.ENGAGEMENT_EVENTS]: 'selective',
    // Sprint 17g-4: UX Remediation
    [NAMESPACES.REFINEMENT_HISTORY]: 'selective',
    [NAMESPACES.PREDICTION_ACCURACY]: 'full',
    [NAMESPACES.CHARACTER_SUMMARY]: 'full',
    // Consumer-refactor Slice 08 — first-run evidence remains device-local.
    [NAMESPACES.WELCOME_OUTCOMES]: 'none',
};
